import { Box, Button, Divider, TextField, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import { formatTime, shortName } from './taskUi';

// Events whose note is people talking to each other; they show under Comments. Plain comments
// show only there; the others are also steps in History.
const COMMENT_ACTIONS = ['COMMENTED', 'COMPLETED', 'SENT_BACK', 'FLAGGED_FOR_PM', 'BLOCKED'];
const COMMENT_CONTEXT = {
    COMPLETED: 'on completing',
    SENT_BACK: 'sending back to',
    FLAGGED_FOR_PM: 'flagging for PM',
    BLOCKED: 'blocking',
};

/** Comments, newest first, with the rest behind Show all. onPost returns the task, or nothing if it didn't save. */
const TaskComments = ({ events, stages, canComment, busy, onPost }) => {
    const [comment, setComment] = useState('');
    const [allOpen, setAllOpen] = useState(false);
    const comments = events
        .filter((e) => COMMENT_ACTIONS.includes(e.action) && e.actorType !== 'SYSTEM' && (e.note || '').trim())
        .reverse();
    if (!canComment && comments.length === 0) return null;
    const post = () =>
        onPost(comment.trim()).then((d) => {
            if (d) setComment('');
        });
    return (
        <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="overline" component="div">
                Comments
            </Typography>
            <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {(allOpen ? comments : comments.slice(0, 1)).map((e) => {
                    const stage = stages.find((s) => s.stageKey === e.stageKey);
                    const context = COMMENT_CONTEXT[e.action];
                    return (
                        <Box component="li" key={e.id} sx={{ py: 0.75 }}>
                            <Typography variant="caption" color="text.secondary" component="div">
                                <b>{shortName(e.actor)}</b>
                                {context && stage ? ` ${context} ${stage.displayName}` : ''}
                                {' · '}
                                <Tooltip title={new Date(e.timestamp).toString()}>
                                    <span>{formatTime(e.timestamp)}</span>
                                </Tooltip>
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {e.note}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
            {comments.length > 1 && (
                <Button size="small" onClick={() => setAllOpen((o) => !o)} aria-expanded={allOpen} sx={{ px: 0 }}>
                    {allOpen ? 'Show newest only' : `Show all ${comments.length}`}
                </Button>
            )}
            {canComment && (
                <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                        size="small"
                        fullWidth
                        multiline
                        maxRows={4}
                        label="Comment"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                    />
                    <Button variant="outlined" disabled={!comment.trim() || busy} onClick={post}>
                        Post
                    </Button>
                </Box>
            )}
        </>
    );
};

export default TaskComments;
